import { useWallet } from '@solana/wallet-adapter-react';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import SeekerLogo from '../../assets/svg/seeker-logo.svg?react';
import { usePublisher } from '../../hooks/usePublisher';
import { PUBLISHER_FROZEN_ACTION_TOOLTIP } from '../../utils/publisherFreeze';
import { getPublisherVerificationGate } from '../../utils/publisherVerification';
import { Button } from '../Button';
import { ConfirmationDialog } from '../ConfirmationDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface NewDappPageHeaderProps {
  isFormTouched: boolean;
  isFormValid: boolean;
  isSubmitting?: boolean;
  formId: string;
  submitLabel?: string;
  canSaveDraft?: boolean;
  onSaveDraft?: () => void;
}

/** Drawer header bar: close, Seeker wordmark, primary submit (shared look with New Version). */
export function NewDappPageHeader({
  isFormTouched,
  isFormValid,
  isSubmitting = false,
  formId,
  submitLabel = 'Save',
  canSaveDraft = false,
  onSaveDraft,
}: NewDappPageHeaderProps) {
  const navigate = useNavigate();
  const wallet = useWallet();
  const { activePublisher } = usePublisher();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleClose = () => {
    if (isFormTouched) {
      setShowConfirmDialog(true);
    } else {
      navigate(-1);
    }
  };

  const isWalletConnected = wallet.connected && wallet.publicKey;
  const isPublisherFrozen = activePublisher?.isFrozen === true;
  const publisherVerification = getPublisherVerificationGate({
    status: activePublisher?.kybStatus,
    publisherType: activePublisher?.publisherType,
    kycKybBypassEnabled: activePublisher?.kycKybBypassEnabled,
  });
  const isButtonDisabled =
    (onSaveDraft ? !canSaveDraft : !isFormValid) ||
    isSubmitting ||
    (!onSaveDraft && !isWalletConnected) ||
    isPublisherFrozen ||
    !publisherVerification.isApproved;
  const disabledTooltip = isPublisherFrozen
    ? PUBLISHER_FROZEN_ACTION_TOOLTIP
    : !publisherVerification.isApproved
      ? publisherVerification.actionMessage
      : !onSaveDraft && !isWalletConnected && !isSubmitting
        ? 'Connect a wallet to save your dApp'
        : null;

  const saveButton = (
    <Button
      type={onSaveDraft ? 'button' : 'submit'}
      onClick={onSaveDraft}
      form={formId}
      variant="primary"
      disabled={isButtonDisabled}
      submitting={isSubmitting}
      className="min-w-[90px] disabled:pointer-events-none"
    >
      {onSaveDraft ? 'Save and continue later' : submitLabel}
    </Button>
  );

  return (
    <>
      <div className="flex h-16 shrink-0 items-center justify-between rounded-t-xl bg-seeker-800 px-6 text-white">
        <button
          type="button"
          aria-label="Close"
          className="flex size-12 items-center justify-center rounded-xl transition-colors duration-150 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          <X className="size-6" />
        </button>
        <SeekerLogo
          className="hidden h-7 w-auto shrink-0 fill-current sm:block"
          aria-label="Seeker"
        />
        <TooltipProvider>
          {disabledTooltip ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">{saveButton}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{disabledTooltip}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            saveButton
          )}
        </TooltipProvider>
      </div>

      <ConfirmationDialog
        isOpen={showConfirmDialog}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave this page?"
        confirmText="Leave"
        cancelText="Stay"
        onConfirm={() => {
          setShowConfirmDialog(false);
          navigate(-1);
        }}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </>
  );
}
